"use client"

import { useState } from "react"
import { InlineMath, BlockMath } from "react-katex"
import "katex/dist/katex.min.css"
import { postAws } from "../postAws"
import AddQuestionMathsExpression from "./AddQuestionMathExpression"

export default function AddQuestion() {
  // Existing form state
  const [questionText, setQuestionText] = useState("")
  const [options, setOptions] = useState(["", "", "", ""])
  const [correctAnswer, setCorrectAnswer] = useState("")
  const [chapterName, setChapterName] = useState("")
  const [subject, setSubject] = useState("")
  const [examType, setExamType] = useState("")
  const [difficulty, setDifficulty] = useState("")
  const [uploadedImage, setUploadedImage] = useState(null)
  const [selectedClass, setSelectedClass] = useState("")
  const [loading, setLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [explanation, setExplanation] = useState("")

  // AI Rendering state
  const [aiRenderingFields, setAiRenderingFields] = useState({})
  const [showAiRenderButton, setShowAiRenderButton] = useState({})
  const [aiRenderingLoading, setAiRenderingLoading] = useState({})

  // Math expression state
  const [showMathExpression, setShowMathExpression] = useState(false)
  const [activeMathField, setActiveMathField] = useState(null)
  const [fieldsWithMath, setFieldsWithMath] = useState({
    question: false,
    "option-0": false,
    "option-1": false,
    "option-2": false,
    "option-3": false,
    explanation: false,
  })

  // Existing handlers
  const handleOptionChange = (index, value) => {
    const newOptions = [...options]
    newOptions[index] = value
    setOptions(newOptions)
    // Detect manual content for AI rendering
    detectManualContent(`option-${index}`, value)
  }

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setUploadedImage(e.target?.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const removeImage = () => {
    setUploadedImage(null)
  }

  const resetForm = () => {
    setQuestionText("")
    setOptions(["", "", "", ""])
    setCorrectAnswer("")
    setChapterName("")
    setSubject("")
    setExamType("")
    setDifficulty("")
    setUploadedImage(null)
    setSelectedClass("")
    setSuccessMessage("")
    setErrorMessage("")
    setExplanation("")
    setFieldsWithMath({
      question: false,
      "option-0": false,
      "option-1": false,
      "option-2": false,
      "option-3": false,
      explanation: false,
    })
    // Clear AI rendering state
    setShowAiRenderButton({})
    setAiRenderingLoading({})
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setSuccessMessage("")
    setErrorMessage("")
    try {
      const formattedOptions = options.map((option, index) => {
        const letter = String.fromCharCode(65 + index)
        return `${letter.toLowerCase()}) ${option}`
      })

      const payload = {
        request: "add-question-in-questionBank",
        body: {
          text: questionText,
          metadata: {
            chapter_name: chapterName,
            correctAnswer: Number.parseInt(correctAnswer),
            ...(selectedClass && { class: selectedClass }),
            ...(subject && { subject: subject }),
            ...(examType && { examType: examType }),
            ...(difficulty && { difficulty_level: difficulty }),
            ...(formattedOptions.length > 0 && { options: formattedOptions }),
            question_type: "mcq",
            has_figure: uploadedImage ? true : false,
            ...(explanation && { explanation: explanation }),
            has_math: // Updated has_math logic
              questionText.includes("$") ||
              questionText.includes("\\(") ||
              questionText.includes("\\[") ||
              options.some((opt) => opt.includes("$") || opt.includes("\\(") || opt.includes("\\[")) ||
              explanation.includes("$") ||
              explanation.includes("\\(") ||
              explanation.includes("\\["),
          },
          ...(uploadedImage && { questionImages3Uri: "s3://bucket-name/questions/manual_upload.png" }),
        },
      }
      console.log("Sending payload:", payload)
      const response = await postAws("https://api-dev.aurait.ai/QuestionBank", "POST", payload)
      console.log("API Response:", response)
      if (response && (response.statusCode === 200 || response.body)) {
        setSuccessMessage("Question added successfully!")
        setTimeout(() => {
          resetForm()
        }, 2000)
      } else {
        setErrorMessage("Failed to add question. Please try again.")
      }
    } catch (err) {
      console.error("API Error:", err)
      setErrorMessage("Failed to add question. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const openMathExpression = (fieldType) => {
    setActiveMathField(fieldType)
    setShowMathExpression(true)
  }

  const closeMathExpression = () => {
    setShowMathExpression(false)
    setActiveMathField(null)
  }

  const insertMathExpression = (mathLatex, isBlock) => {
    const mathExpression = isBlock ? `$$${mathLatex}$$` : `$${mathLatex}$`
    // Mark this field as having math inserted via floating icon
    setFieldsWithMath((prev) => ({
      ...prev,
      [activeMathField]: true,
    }))
    switch (activeMathField) {
      case "question":
        setQuestionText((prev) => prev + mathExpression)
        break
      case "option-0":
      case "option-1":
      case "option-2":
      case "option-3":
        const optionIndex = Number.parseInt(activeMathField.split("-")[1])
        const newOptions = [...options]
        newOptions[optionIndex] = newOptions[optionIndex] + mathExpression
        setOptions(newOptions)
        break
      case "explanation":
        setExplanation((prev) => prev + mathExpression)
        break
    }
    closeMathExpression()
  }

  const renderMathInText = (text) => {
    if (!text) return text
    // Regex to split text by all four types of math delimiters, ensuring they are captured.
    // For literal `\(` and `\[`, we need to escape the backslash in the regex pattern.
    const parts = text.split(/(\$\$[^$]*?\$\$|\$[^$]*?\$|\\$$[^\\]*?\\$$|\\\[[^\\]]*?\\\])/g)

    return parts.map((part, index) => {
      if (!part) return null // Handle empty strings from split

      // Check for display math delimiters first
      if (part.startsWith("$$") && part.endsWith("$$")) {
        const latex = part.slice(2, -2)
        try {
          return (
            <div key={index} style={{ margin: "8px 0", textAlign: "center" }}>
              <BlockMath math={latex} />
            </div>
          )
        } catch (e) {
          return (
            <span key={index} style={{ color: "red" }}>
              [Math Error: {latex}]
            </span>
          )
        }
      } else if (part.startsWith("\\[") && part.endsWith("\\]")) {
        const latex = part.slice(2, -2) // Remove '\[ ' and ' \]'
        try {
          return (
            <div key={index} style={{ margin: "8px 0", textAlign: "center" }}>
              <BlockMath math={latex} />
            </div>
          )
        } catch (e) {
          return (
            <span key={index} style={{ color: "red" }}>
              [Math Error: {latex}]
            </span>
          )
        }
      }
      // Then check for inline math delimiters
      else if (part.startsWith("$") && part.endsWith("$")) {
        const latex = part.slice(1, -1)
        try {
          return <InlineMath key={index} math={latex} />
        } catch (e) {
          return (
            <span key={index} style={{ color: "red" }}>
              [Math Error: {latex}]
            </span>
          )
        }
      } else if (part.startsWith("$$") && part.endsWith("$$")) {
        // Corrected check for literal $$...$$
        const latex = part.slice(2, -2) // Remove '$$' and '$$'
        try {
          return <InlineMath key={index} math={latex} />
        } catch (e) {
          return (
            <span key={index} style={{ color: "red" }}>
              [Math Error: {latex}]
            </span>
          )
        }
      } else {
        // Regular text - split by line breaks
        return part.split("\n").map((line, lineIndex, lines) => (
          <span key={`${index}-${lineIndex}`}>
            {line}
            {lineIndex < lines.length - 1 && <br />}
          </span>
        ))
      }
    })
  }

  // Math Input Component
  const MathInput = ({ fieldType }) => {
    return (
      <button
        type="button"
        onClick={() => openMathExpression(fieldType)}
        style={{
          position: "absolute",
          right: fieldType.startsWith("option") ? "120px" : "8px",
          top: "50%",
          transform: "translateY(-50%)",
          background: "#a855f7",
          color: "white",
          border: "none",
          borderRadius: "50%",
          width: "28px",
          height: "28px",
          cursor: "pointer",
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
        title="Add Math Expression"
      >
        ∑
      </button>
    )
  }

  // AI Render Button Component
  const AIRenderButton = ({ fieldType }) => {
    if (!showAiRenderButton[fieldType]) return null
    return (
      <button
        type="button"
        onClick={() => renderWithAI(fieldType)}
        disabled={aiRenderingLoading[fieldType]}
        style={{
          position: "absolute",
          right: fieldType.startsWith("option") ? "160px" : "48px",
          top: "50%",
          transform: "translateY(-50%)",
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          color: "white",
          border: "none",
          borderRadius: "6px",
          padding: "6px 12px",
          cursor: aiRenderingLoading[fieldType] ? "not-allowed" : "pointer",
          fontSize: "11px",
          fontWeight: "500",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          zIndex: 10,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          whiteSpace: "nowrap",
        }}
        title="Render with AI"
      >
        {aiRenderingLoading[fieldType] ? (
          <>
            <span style={{ animation: "spin 1s linear infinite" }}>⟳</span>
            AI...
          </>
        ) : (
          <>🤖 Render AI</>
        )}
      </button>
    )
  }

  // AI Rendering Functions
  const detectManualContent = (fieldType, content) => {
    // Check if content was added manually (not via floating math button)
    if (content && !fieldsWithMath[fieldType]) {
      setShowAiRenderButton((prev) => ({ ...prev, [fieldType]: true }))
    } else {
      setShowAiRenderButton((prev) => ({ ...prev, [fieldType]: false }))
    }
  }

  const renderWithAI = async (fieldType) => {
    let currentContent = ""
    // Get current content based on field type
    switch (fieldType) {
      case "question":
        currentContent = questionText
        break
      case "option-0":
      case "option-1":
      case "option-2":
      case "option-3":
        const optionIndex = Number.parseInt(fieldType.split("-")[1])
        currentContent = options[optionIndex]
        break
      case "explanation":
        currentContent = explanation
        break
    }

    if (!currentContent.trim()) return

    setAiRenderingLoading((prev) => ({ ...prev, [fieldType]: true }))
    try {
      const payload = {
        request: "convert-doc-to-markdown",
        body: {
          docContent: currentContent,
        },
      }
      console.log("AI Rendering payload:", payload)
      const response = await postAws("https://api-dev.aurait.ai/QuestionBank", "POST", payload)
      console.log("AI Rendering response:", response)
      if (response && response.statusCode === 200 && response.markdown) {
        // Replace content with AI-rendered markdown
        switch (fieldType) {
          case "question":
            setQuestionText(response.markdown)
            break
          case "option-0":
          case "option-1":
          case "option-2":
          case "option-3":
            const optionIndex = Number.parseInt(fieldType.split("-")[1])
            const newOptions = [...options]
            newOptions[optionIndex] = response.markdown
            setOptions(newOptions)
            break
          case "explanation":
            setExplanation(response.markdown)
            break
        }
        // Mark field as having math content for preview
        setFieldsWithMath((prev) => ({ ...prev, [fieldType]: true }))
        setShowAiRenderButton((prev) => ({ ...prev, [fieldType]: false }))
        // Show success message briefly
        setSuccessMessage("Content rendered with AI successfully!")
        setTimeout(() => setSuccessMessage(""), 3000)
      } else {
        setErrorMessage("Failed to render content with AI. Please try again.")
        setTimeout(() => setErrorMessage(""), 5000)
      }
    } catch (error) {
      console.error("AI Rendering Error:", error)
      setErrorMessage("Failed to add question. Please try again.")
      setTimeout(() => setErrorMessage(""), 5000)
    } finally {
      setAiRenderingLoading((prev) => ({ ...prev, [fieldType]: false }))
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        padding: "20px",
        marginTop: "-20px",
      }}
    >
      <div
        style={{
          width: "100%",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #a855f7 0%, #ec4899 100%)",
            color: "white",
            padding: "16px 24px",
            fontSize: "18px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>+</span>
          Add New Question with Smart Math Support
        </div>
        {/* Form Content */}
        <div style={{ padding: "24px" }}>
          <form onSubmit={handleSubmit}>
            {/* Success/Error Messages */}
            {successMessage && (
              <div
                style={{
                  backgroundColor: "#dcfce7",
                  border: "1px solid #bbf7d0",
                  color: "#166534",
                  padding: "12px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                }}
              >
                <strong>Success:</strong> {successMessage}
              </div>
            )}
            {errorMessage && (
              <div
                style={{
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  color: "#dc2626",
                  padding: "12px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                }}
              >
                <strong>Error:</strong> {errorMessage}
              </div>
            )}
            {/* Single Row: Class, Subject, Chapter, Exam Type, Difficulty */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Class
                </label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    backgroundColor: "white",
                    color: "#6b7280",
                  }}
                  required
                >
                  <option value="">Select class</option>
                  <option value="6">Class 6th</option>
                  <option value="7">Class 7th</option>
                  <option value="8">Class 8th</option>
                  <option value="9">Class 9th</option>
                  <option value="10">Class 10th</option>
                  <option value="11">Class 11th</option>
                  <option value="12">Class 12th</option>
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Subject
                </label>
                <input
                  type="text"
                  placeholder="Subject name"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                  required
                />
              </div>
              <div>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Chapter
                </label>
                <input
                  type="text"
                  placeholder="Chapter name"
                  value={chapterName}
                  onChange={(e) => setChapterName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                  required
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Exam Type
                </label>
                <input
                  type="text"
                  placeholder="e.g., Midterm, Final, Quiz"
                  value={examType}
                  onChange={(e) => setExamType(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    outline: "none",
                  }}
                  required
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Difficulty Level
                </label>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    backgroundColor: "white",
                    color: "#6b7280",
                  }}
                  required
                >
                  <option value="">Select difficulty</option>
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
            </div>
            {/* Question Text and Upload Image Labels */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
                marginTop: "-14px",
              }}
            >
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                }}
              >
                Question Text
              </label>
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                }}
              >
                Upload Image
              </label>
            </div>
            {/* Question Text Area with Math Support and Upload */}
            <div
              style={{
                display: "flex",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              <div style={{ flex: 1, position: "relative" }}>
                <textarea
                  placeholder="Enter your question here..."
                  value={questionText}
                  onChange={(e) => {
                    setQuestionText(e.target.value)
                    detectManualContent("question", e.target.value)
                  }}
                  style={{
                    width: "100%",
                    height: "80px",
                    padding: "10px 40px 10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    resize: "none",
                    outline: "none",
                    fontFamily: "inherit",
                  }}
                  required
                />
                <MathInput fieldType="question" />
                <AIRenderButton fieldType="question" />
              </div>
              <div>
                {!uploadedImage ? (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: "none" }}
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "80px",
                        height: "80px",
                        border: "2px dashed #c084fc",
                        borderRadius: "6px",
                        cursor: "pointer",
                        backgroundColor: "#faf5ff",
                      }}
                    >
                      <span style={{ fontSize: "24px", color: "#a855f7" }}>🖼️</span>
                    </label>
                  </div>
                ) : (
                  <div style={{ position: "relative" }}>
                    <img
                      src={uploadedImage || "/placeholder.svg"}
                      alt="Uploaded"
                      style={{
                        width: "80px",
                        height: "80px",
                        objectFit: "cover",
                        borderRadius: "6px",
                        border: "2px solid #22c55e",
                      }}
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-8px",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "#ef4444",
                        color: "white",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
            {/* Question Preview - Only show if math was inserted via floating icon */}
            {fieldsWithMath.question && questionText && (
              <div
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  fontSize: "14px",
                  minHeight: "40px",
                  marginTop: "8px",
                }}
              >
                <strong>Preview:</strong> {renderMathInText(questionText)}
              </div>
            )}
            {/* Answer Options */}
            <div style={{ marginBottom: "24px" }}>

              {/* Options in 2x2 grid */}
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
                {/* Row 1: Options A and B */}
                <div style={{ display: "flex", gap: "12px" }}>
                  {[0, 1].map((index) => (
                    <div
                      key={index}
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        backgroundColor: "#f9fafb",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "white",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        {String.fromCharCode(65 + index)}
                      </div>
                      <input
                        type="text"
                        placeholder={`Option ${String.fromCharCode(65 + index)}`}
                        value={options[index]}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        style={{
                          flex: 1,
                          padding: "8px 140px 8px 8px",
                          border: "none",
                          backgroundColor: "transparent",
                          fontSize: "14px",
                          outline: "none",
                        }}
                        required
                      />
                      <MathInput fieldType={`option-${index}`} />
                      <AIRenderButton fieldType={`option-${index}`} />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          position: "absolute",
                          right: "12px",
                        }}
                      >
                        <input
                          type="radio"
                          name="correctAnswer"
                          value={index.toString()}
                          checked={correctAnswer === index.toString()}
                          onChange={(e) => setCorrectAnswer(e.target.value)}
                          style={{ accentColor: "#22c55e" }}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#22c55e",
                            fontWeight: "500",
                          }}
                        >
                          Correct
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Option Preview - Only show if math was inserted via floating icon */}
                {fieldsWithMath[`option-${0}`] && options[0] && (
                  <div
                    style={{
                      padding: "6px 8px",
                      backgroundColor: "#f3f4f6",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "12px",
                      minHeight: "20px",
                      marginTop: "4px",
                    }}
                  >
                    {renderMathInText(options[0])}
                  </div>
                )}
                {fieldsWithMath[`option-${1}`] && options[1] && (
                  <div
                    style={{
                      padding: "6px 8px",
                      backgroundColor: "#f3f4f6",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "12px",
                      minHeight: "20px",
                      marginTop: "4px",
                    }}
                  >
                    {renderMathInText(options[1])}
                  </div>
                )}
                {/* Row 2: Options C and D */}
                <div style={{ display: "flex", gap: "12px" }}>
                  {[2, 3].map((index) => (
                    <div
                      key={index}
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        backgroundColor: "#f9fafb",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "white",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        {String.fromCharCode(65 + index)}
                      </div>
                      <input
                        type="text"
                        placeholder={`Option ${String.fromCharCode(65 + index)}`}
                        value={options[index]}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        style={{
                          flex: 1,
                          padding: "8px 140px 8px 8px",
                          border: "none",
                          backgroundColor: "transparent",
                          fontSize: "14px",
                          outline: "none",
                        }}
                        required
                      />
                      <MathInput fieldType={`option-${index}`} />
                      <AIRenderButton fieldType={`option-${index}`} />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          position: "absolute",
                          right: "12px",
                        }}
                      >
                        <input
                          type="radio"
                          name="correctAnswer"
                          value={index.toString()}
                          checked={correctAnswer === index.toString()}
                          onChange={(e) => setCorrectAnswer(e.target.value)}
                          style={{ accentColor: "#22c55e" }}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#22c55e",
                            fontWeight: "500",
                          }}
                        >
                          Correct
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Option Preview - Only show if math was inserted via floating icon */}
                {fieldsWithMath[`option-${2}`] && options[2] && (
                  <div
                    style={{
                      padding: "6px 8px",
                      backgroundColor: "#f3f4f6",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "12px",
                      minHeight: "20px",
                      marginTop: "4px",
                    }}
                  >
                    {renderMathInText(options[2])}
                  </div>
                )}
                {fieldsWithMath[`option-${3}`] && options[3] && (
                  <div
                    style={{
                      padding: "6px 8px",
                      backgroundColor: "#f3f4f6",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "12px",
                      minHeight: "20px",
                      marginTop: "4px",
                    }}
                  >
                    {renderMathInText(options[3])}
                  </div>
                )}
              </div>
              {/* Explanation Section */}
              <div style={{ position: "relative" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "8px",
                  }}
                >
                  Explanation (Why is this answer correct?)
                </label>
                <textarea
                  placeholder="Provide an explanation for why the correct answer is right..."
                  value={explanation}
                  onChange={(e) => {
                    setExplanation(e.target.value)
                    detectManualContent("explanation", e.target.value)
                  }}
                  style={{
                    width: "100%",
                    height: "80px",
                    padding: "10px 40px 10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    resize: "none",
                    outline: "none",
                    fontFamily: "inherit",
                    backgroundColor: "#fefefe",
                  }}
                />
                <MathInput fieldType="explanation" />
                <AIRenderButton fieldType="explanation" />
              </div>
              {/* Explanation Preview - Only show if math was inserted via floating icon */}
              {fieldsWithMath.explanation && explanation && (
                <div
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "14px",
                    minHeight: "40px",
                    marginTop: "8px",
                  }}
                >
                  <strong>Preview:</strong> {renderMathInText(explanation)}
                </div>
              )}
            </div>
            {/* Submit Button */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  background: loading ? "#9ca3af" : "linear-gradient(135deg, #a855f7 0%, #ec4899 100%)",
                  color: "white",
                  padding: "12px 24px",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {loading ? (
                  <>
                    <span style={{ animation: "spin 1s linear infinite" }}>⟳</span>
                    Adding Question...
                  </>
                ) : (
                  <>
                    <span>+</span>
                    Add Question to Bank
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* Math Expression Component */}
      {showMathExpression && (
        <AddQuestionMathsExpression
          onClose={closeMathExpression}
          onInsert={insertMathExpression}
          fieldType={activeMathField}
        />
      )}
    </div>
  )
}
